import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { SiteSessionTypeSchema } from '../site-sessions/site-session-schemas'

export const creditTypeEnumSchema = z
  .enum(['UNLIMITED', 'LIMITED'])
  .register(asEnumType, { name: 'CreditType' })
export type CreditTypeEnum = z.infer<typeof creditTypeEnumSchema>

export const creditExpirationTypeEnumSchema = z
  .enum(['ON_NEXT_ALLOCATION', 'CUSTOM'])
  .register(asEnumType, { name: 'CreditExpirationType' })
export type CreditExpirationTypeEnum = z.infer<typeof creditExpirationTypeEnumSchema>

export const creditExpirationUnitEnumSchema = z
  .enum(['YEARS', 'MONTHS', 'WEEKS', 'DAYS'])
  .register(asEnumType, { name: 'CreditExpirationUnit' })
export type CreditExpirationUnitEnum = z.infer<typeof creditExpirationUnitEnumSchema>

export const serviceCreditTemplateSchema = z
  .object({
    serviceCreditTemplateId: z.string(),
    serviceTemplateId: z.string(),
    creditsType: creditTypeEnumSchema,
    amount: z.number().int().min(1).nullable(),
    siteSessionTypeIds: z.array(SiteSessionTypeSchema.shape.siteSessionTypeId).min(1),
    expirationEnabled: z.boolean(),
    expirationType: creditExpirationTypeEnumSchema.nullable(),
    expirationAmount: z.number().int().min(1).nullable(),
    expirationUnit: creditExpirationUnitEnumSchema.nullable(),
    order: z.number().int().min(1),
    createdAt: z.date(),
  })
  .register(asObjectType, { name: 'ServiceCreditTemplate' })

export type ServiceCreditTemplateSchema = z.infer<typeof serviceCreditTemplateSchema>

const serviceCreditTemplateBaseSchema = z
  .object({
    creditsType: creditTypeEnumSchema,
    amount: z.number().int().min(1).optional(),
    siteSessionTypeIds: z.array(SiteSessionTypeSchema.shape.siteSessionTypeId).min(1),
    expirationEnabled: z.boolean().default(false),
    expirationType: creditExpirationTypeEnumSchema.optional(),
    expirationAmount: z.number().int().min(1).optional(),
    expirationUnit: creditExpirationUnitEnumSchema.optional(),
    order: z.number().int().min(1),
  })
  .superRefine((data, ctx) => {
    if (data.creditsType === 'LIMITED' && !data.amount) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'amount is required when creditsType is LIMITED',
      })
    }

    if (data.creditsType === 'UNLIMITED' && data.amount !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'amount should not be set when creditsType is UNLIMITED',
      })
    }

    if (data.expirationEnabled) {
      if (!data.expirationType) {
        ctx.addIssue({
          code: 'custom',
          path: ['expirationType'],
          message: 'expirationType is required when expirationEnabled is true',
        })
      }

      if (data.expirationType === 'CUSTOM') {
        if (!data.expirationAmount) {
          ctx.addIssue({
            code: 'custom',
            path: ['expirationAmount'],
            message: 'expirationAmount is required when expirationType is CUSTOM',
          })
        }
        if (!data.expirationUnit) {
          ctx.addIssue({
            code: 'custom',
            path: ['expirationUnit'],
            message: 'expirationUnit is required when expirationType is CUSTOM',
          })
        }
      }

      if (data.expirationType === 'ON_NEXT_ALLOCATION') {
        if (data.expirationAmount !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['expirationAmount'],
            message: 'expirationAmount should not be set when expirationType is ON_NEXT_ALLOCATION',
          })
        }
        if (data.expirationUnit !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['expirationUnit'],
            message: 'expirationUnit should not be set when expirationType is ON_NEXT_ALLOCATION',
          })
        }
      }
    } else {
      if (data.expirationType !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['expirationType'],
          message: 'expirationType should not be set when expirationEnabled is false',
        })
      }
      if (data.expirationAmount !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['expirationAmount'],
          message: 'expirationAmount should not be set when expirationEnabled is false',
        })
      }
      if (data.expirationUnit !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['expirationUnit'],
          message: 'expirationUnit should not be set when expirationEnabled is false',
        })
      }
    }
  })

export const serviceCreditTemplateCreateSchema = serviceCreditTemplateBaseSchema

export type ServiceCreditTemplateCreateSchema = z.infer<typeof serviceCreditTemplateCreateSchema>

// Input schema for create/update operations (includes optional ID for updates)
export const serviceCreditTemplateInputSchema = serviceCreditTemplateBaseSchema.safeExtend({
  serviceCreditTemplateId: z.string().optional(),
})

export type ServiceCreditTemplateInputSchema = z.infer<typeof serviceCreditTemplateInputSchema>

/**
 * Bulk-import wrapper input for ServiceCreditTemplate.
 *
 * Differs from {@link serviceCreditTemplateCreateSchema} (the UI write path's
 * per-row input) in two ways:
 *  - Caller MUST provide `serviceCreditTemplateId` and `serviceTemplateId`
 *    (deterministic from the transformer, required for retry idempotency and
 *    so the credit can be written in the same batch as its parent template).
 *  - The `.superRefine` validations on the base schema apply pre-write at the
 *    wrapper layer; the repo bulk method only sees this flat shape.
 */
export const serviceCreditTemplateBulkCreateForImportSchema = z.object({
  serviceCreditTemplateId: z.string(),
  serviceTemplateId: z.string(),
  creditsType: creditTypeEnumSchema,
  amount: z.number().int().min(1).nullable(),
  siteSessionTypeIds: z.array(SiteSessionTypeSchema.shape.siteSessionTypeId).min(1),
  expirationEnabled: z.boolean(),
  expirationType: creditExpirationTypeEnumSchema.nullable(),
  expirationAmount: z.number().int().min(1).nullable(),
  expirationUnit: creditExpirationUnitEnumSchema.nullable(),
  order: z.number().int().min(1),
})

export type ServiceCreditTemplateBulkCreateForImportSchema = z.infer<
  typeof serviceCreditTemplateBulkCreateForImportSchema
>
